/**
 * Sprint 72 — Branding whitelabel pour PDFs juridiques.
 * Logo tenant + couleur d'accent + pied de page. Gated par `legal_whitelabel`.
 */
import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, Palette, Save } from "lucide-react";
import { toast } from "sonner";

export default function LegalBrandingCard() {
  const [state, setState] = useState({
    logo_base64: null, brand_color: "#1e40af", footer_text: "",
    configured: false, loading: true,
  });
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/legal/branding");
      setState({
        logo_base64: r.data.logo_base64 || null,
        brand_color: r.data.brand_color || "#1e40af",
        footer_text: r.data.footer_text || "",
        configured: r.data.configured,
        loading: false,
      });
    } catch (e) {
      // 403 = feature not included → géré par global upsell handler
      setState((s) => ({ ...s, loading: false }));
    }
  };
  useEffect(() => { load(); }, []);

  const onLogoPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) {
      toast.error("Logo trop volumineux (max 500 Ko). Compressez-le d'abord.");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setState((s) => ({ ...s, logo_base64: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try {
      const r = await api.put("/legal/branding", {
        logo_base64: state.logo_base64,
        brand_color: state.brand_color,
        footer_text: state.footer_text,
      });
      setState({ ...r.data, loading: false });
      toast.success("Branding enregistré — vos PDFs porteront désormais votre identité.");
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeLogo = () => setState((s) => ({ ...s, logo_base64: null }));

  const removeAll = async () => {
    if (!window.confirm("Retirer complètement le branding personnalisé ?")) return;
    try {
      await api.delete("/legal/branding");
      toast.success("Branding retiré");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (state.loading) {
    return <Card><CardContent className="p-6 text-sm text-slate-500">Chargement…</CardContent></Card>;
  }

  return (
    <Card data-testid="legal-branding-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Palette className="w-4 h-4 text-purple-600" />
          Branding whitelabel PDF
        </CardTitle>
        <p className="text-sm text-slate-500">
          Personnalisez vos PV, convocations et résolutions avec votre logo et vos couleurs.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label>Logo (max 500 Ko · PNG/JPEG)</Label>
            <div className="mt-2 border-2 border-dashed rounded-lg p-4 text-center bg-slate-50 min-h-[140px] flex items-center justify-center">
              {state.logo_base64 ? (
                <div className="space-y-2">
                  <img src={state.logo_base64} alt="Logo"
                       className="max-h-24 max-w-full mx-auto object-contain"
                       data-testid="logo-preview" />
                  <Button variant="outline" size="sm" onClick={removeLogo}
                          data-testid="remove-logo-btn">
                    <Trash2 className="w-3 h-3 mr-1" />Retirer
                  </Button>
                </div>
              ) : (
                <Button variant="outline" onClick={() => fileRef.current?.click()}
                        data-testid="upload-logo-btn">
                  <Upload className="w-4 h-4 mr-2" />Téléverser le logo
                </Button>
              )}
              <input id="legal-logo-file" name="legal-logo-file" ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg"
                     onChange={onLogoPick} className="hidden" />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>Couleur d'accent (titres H2 des PDF)</Label>
              <div className="flex items-center gap-2 mt-2">
                <input id="brand-color-input" name="brand_color" type="color" value={state.brand_color}
                       onChange={(e) => setState({ ...state, brand_color: e.target.value })}
                       className="h-10 w-14 rounded border cursor-pointer"
                       data-testid="brand-color-input" />
                <Input value={state.brand_color}
                       onChange={(e) => setState({ ...state, brand_color: e.target.value })}
                       maxLength={7} className="w-32 font-mono" />
              </div>
            </div>
            <div>
              <Label>Pied de page (max 150 caractères)</Label>
              <Input value={state.footer_text}
                     onChange={(e) => setState({ ...state, footer_text: e.target.value })}
                     placeholder="Ma Société SAS · 12 rue X · 75001 Paris"
                     maxLength={150}
                     data-testid="footer-text-input" />
            </div>
          </div>
        </div>

        <div className="flex justify-between pt-2">
          {state.configured ? (
            <Button variant="outline" size="sm" onClick={removeAll}
                     className="text-red-600 hover:bg-red-50"
                     data-testid="remove-branding-btn">
              <Trash2 className="w-4 h-4 mr-2" />Retirer le branding
            </Button>
          ) : <span />}
          <Button onClick={save} data-testid="save-branding-btn">
            <Save className="w-4 h-4 mr-2" />Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
